// Universal AI Tuner Pro homepage — 9-section premium landing
// Replaces the prior BMW-specialized landing. Emphasizes HYBRID intelligence
// (database acceleration + AI discovery) and universal vehicle coverage.
import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  UploadCloud, Cpu, MessageSquare, Zap, ShieldCheck, History, Wrench,
  Tractor, Ship, Bike, Truck, Car, ArrowRight, Play, Database, Sparkles,
  CheckCircle2, TrendingUp, Users, Activity, ChevronDown, Gauge,
  Brain, HandMetal, AlertTriangle, Usb, Smartphone, Server, Cloud, ExternalLink,
} from 'lucide-react';

const FADE_UP = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-80px' }, transition: { duration: 0.6, ease: 'easeOut' } };

const HERO_IMG = '/brand/ai-tuner-pro-logo.jpg';
const PROOF_CAR = 'https://images.unsplash.com/photo-1760199129864-df2332e3f3e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxwZXJmb3JtYW5jZSUyMGNhciUyMGRhcmslMjBtb29keXxlbnwwfHx8fDE3NzY5NTg4NDZ8MA&ixlib=rb-4.1.0&q=85';
const PROOF_BIKE = 'https://images.unsplash.com/photo-1568772585471-4f4169aed2a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxtb3RvcmN5Y2xlJTIwc3BvcnRiaWtlJTIwZGFya3xlbnwwfHx8fDE3NzY5NTg4NDZ8MA&ixlib=rb-4.1.0&q=85';
const PROOF_TRUCK = 'https://images.pexels.com/photos/12954621/pexels-photo-12954621.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const ENGINE_BAY = 'https://images.pexels.com/photos/15393572/pexels-photo-15393572.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

// ========== HERO ==========
function Hero({ onUpload, onDemo }) {
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#0A0A0A]">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="AI Tuner Pro" className="w-full h-full object-cover opacity-55" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/75 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
        {/* scanline shimmer */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,122,255,0.03) 0px, rgba(0,122,255,0.03) 1px, transparent 1px, transparent 3px)' }} />
      </div>
      {/* floating glow nodes */}
      <motion.div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-[#007AFF]/10 blur-[100px]" animate={{ y: [0, 20, 0], opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 6, repeat: Infinity }} />
      <motion.div className="absolute bottom-1/3 right-1/3 w-48 h-48 rounded-full bg-[#FF5E1A]/15 blur-[80px]" animate={{ y: [0, -15, 0], opacity: [0.4, 0.7, 0.4] }} transition={{ duration: 5, repeat: Infinity, delay: 1 }} />

      <div className="relative z-10 container mx-auto px-6 pt-24 pb-16">
        <div className="max-w-4xl">
          <motion.div {...FADE_UP} className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#007AFF]" />
            <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-[#007AFF]">Built for BMW MSV80 today · expanding coverage</span>
          </motion.div>
          <motion.h1 {...FADE_UP} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-['Outfit'] text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[1.1] sm:leading-[1.05] text-white mb-6">
            AI ECU tuning for <span className="bg-gradient-to-r from-[#007AFF] via-[#4DA3FF] to-[#FF5E1A] bg-clip-text text-transparent">real binaries.</span><br />
            Datalog-driven iteration.
          </motion.h1>
          <motion.p {...FADE_UP} transition={{ duration: 0.7, delay: 0.2 }}
            className="text-base sm:text-lg lg:text-xl text-zinc-300 max-w-2xl mb-8 sm:mb-10 leading-relaxed">
            Upload your bin. We score the health of the setup, match known tables instantly, and generate a safer starting tune. Iterate with real datalogs. <span className="text-white font-semibold">You flash with your own hardware.</span>
          </motion.p>
          <motion.div {...FADE_UP} transition={{ duration: 0.7, delay: 0.3 }} className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
            <button onClick={onUpload} data-testid="hero-upload-bin-btn"
              className="group relative px-6 sm:px-8 py-3.5 sm:py-4 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white font-bold text-sm tracking-[0.12em] uppercase rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_45px_rgba(255,94,26,0.35)] flex items-center justify-center gap-2">
              <UploadCloud className="w-4 h-4" />
              Upload Your Bin
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={onDemo} data-testid="hero-watch-demo-btn"
              className="group px-6 sm:px-8 py-3.5 sm:py-4 border border-[#007AFF]/40 hover:border-[#007AFF] text-white font-bold text-sm tracking-[0.12em] uppercase rounded-sm transition-all duration-200 hover:bg-[#007AFF]/10 flex items-center justify-center gap-2">
              <Play className="w-4 h-4 text-[#007AFF]" />
              Watch Demo
            </button>
          </motion.div>
          {/* Hero-bottom metric strip — concrete, defensible numbers */}
          <motion.div {...FADE_UP} transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-14 grid grid-cols-3 gap-4 max-w-2xl border-t border-[#27272A] pt-6">
            {[
              { n: '1000+', l: 'Known ECU tables' },
              { n: '12', l: 'Tune revisions iterated' },
              { n: 'MSV80', l: 'Live ECU today' },
            ].map((m) => (
              <div key={m.l}>
                <div className="font-['Outfit'] text-2xl sm:text-3xl font-black text-white">{m.n}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-wider font-['JetBrains_Mono']">{m.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
      <ChevronDown className="absolute bottom-6 left-1/2 -translate-x-1/2 w-5 h-5 text-zinc-600 animate-bounce" />
    </section>
  );
}

// ========== TRUST BAR ==========
function TrustBar() {
  const items = ['Hybrid Database + AI', 'BMW MSV80 live · more ECUs coming', 'User-contributed definitions', 'Safety checks on every tune', 'Encrypted uploads'];
  return (
    <section className="bg-[#050505] border-y border-[#18181B] py-5">
      <div className="container mx-auto px-6">
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs sm:text-sm font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-zinc-500">
          {items.map((it, i) => (
            <React.Fragment key={it}>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-[#007AFF]" />{it}</span>
              {i < items.length - 1 && <span className="hidden sm:inline text-zinc-800">•</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

// ========== HOW IT WORKS ==========
function HowItWorks() {
  const steps = [
    { n: '01', icon: UploadCloud, title: 'Upload Any Binary', text: 'Drag-drop any .bin, .ols, .kp, Damos, A2L, or XDF file. We support OBD, bench, and boot-mode reads from any make.', glow: '#007AFF' },
    { n: '02', icon: Cpu, title: 'Hybrid Engine Runs', text: 'Our database instantly labels known tables. AI reasoning discovers the unknowns — fuel, timing, boost, limits, rev cuts, DTCs.', glow: '#FF5E1A' },
    { n: '03', icon: MessageSquare, title: 'Chat Your Goals', text: 'More power? E85 conversion? Emissions delete? Economy? Rev-limit bump? The copilot knows your ECU and your vehicle.', glow: '#007AFF' },
    { n: '04', icon: Zap, title: 'Iterate & Flash', text: 'Upload datalogs → AI refines the tune → versioned output → download flash-ready bin. Rollback anytime.', glow: '#FF5E1A' },
  ];
  return (
    <section id="how-it-works" className="py-24 bg-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-2xl mb-14">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#007AFF] mb-3">// How It Works</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">Four steps from binary to power.</h2>
          <p className="text-lg text-zinc-400 leading-relaxed">No templates. No vehicle-specific demos. Just your binary, our hybrid intelligence, and your goals.</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <motion.div key={s.n} {...FADE_UP} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative p-6 bg-[#121212] border border-[#27272A] rounded-sm hover:border-[#007AFF]/50 transition-all hover:-translate-y-1">
              <div className="flex items-start justify-between mb-6">
                <div className="w-11 h-11 flex items-center justify-center rounded-sm border border-[#27272A] group-hover:border-[#007AFF]/40 transition-colors">
                  <s.icon className="w-5 h-5" style={{ color: s.glow }} />
                </div>
                <span className="font-['JetBrains_Mono'] text-3xl font-bold text-[#27272A] group-hover:text-[#007AFF]/30 transition-colors">{s.n}</span>
              </div>
              <h3 className="font-['Outfit'] text-lg font-bold text-white mb-2">{s.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ========== KEY FEATURES ==========
function Features() {
  const feats = [
    { icon: Database, title: 'Hybrid Database Acceleration', text: 'Pre-loaded + user-contributed tables/maps for thousands of ECUs. Known binaries load in seconds.' },
    { icon: Sparkles, title: 'Full AI Map Discovery', text: 'Unknown binary? AI reasons from first principles — MAF curves, timing patterns, byte entropy — to find every map.' },
    { icon: UploadCloud, title: 'Contribute & Earn', text: 'Upload Damos / A2L / XDF raw definitions. Earn credits + contributor badges. Grow the tuning internet.' },
    { icon: MessageSquare, title: 'Chat-Based Iteration', text: 'Real conversation. Upload logs. AI proposes adjustments with specific cell-by-cell justifications.' },
    { icon: ShieldCheck, title: 'Safety Checks Built-In', text: 'Rev limits, boost ceilings, AFR targets, knock protection — all validated before any tune ships.' },
    { icon: History, title: 'Version Control & Rollback', text: 'Every tune is versioned. Side-by-side diffs. One-click rollback to any prior calibration.' },
    { icon: Wrench, title: 'OBD / Bench / Boot Modes', text: 'Procedure guidance for BTT Quickflash, Autotuner, Flex, KESSv2, Dimsport, EVC and more.' },
    { icon: Gauge, title: 'Truly Universal', text: 'Cars, trucks, motorcycles, marine, tractors, industrial. If it has an ECU binary, we tune it.' },
  ];
  return (
    <section className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#060606] to-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mb-16">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// Key Features</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white">The professional tuner's toolkit — entirely AI-native.</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {feats.map((f, i) => (
            <motion.div key={f.title} {...FADE_UP} transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
              className="group p-5 bg-[#121212] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/40 hover:bg-[#141414] transition-all">
              <f.icon className="w-5 h-5 text-[#FF5E1A] mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-['Outfit'] text-base font-bold text-white mb-2">{f.title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ========== PROOF — receipts from real iteration ==========
function Proof() {
  const receipts = [
    {
      title: 'V11 → V12 mid-range AFR target hit',
      tag: 'TUNE ITERATION',
      bullet: 'May 2026: datalogs showed mid-range AFR 13.05 vs target 11.5 (lean). Applied +30% enrichment to lambda rows 10-13. Result: 5500-6000 RPM band now hits 11.91 AFR. Full pull saved to scorecard.',
      meta: '40-byte diff · 5500-6400 RPM band',
      color: 'emerald',
    },
    {
      title: 'AI-built next revision in 3 seconds',
      tag: 'AI COPILOT',
      bullet: '"Build Next Revision" button reads latest datalog, computes AFR delta vs target, sizes enrichment + timing. Generated V13_AI_Enr17_T3p0 from V12 data: "Mid-range AFR 12.91, delta +1.41 → 1.17× enrichment. Knock 1.82V severe → -2.25° timing rows 12-15."',
      meta: '78 bytes changed · zero hand-coding',
      color: 'purple',
    },
    {
      title: 'Map Discovery on unknown binaries',
      tag: 'MAP DISCOVERY',
      bullet: 'Community XDF for MSV80 was ~25% wrong — pointing at VANOS table instead of fuel map. Heuristic scanner finds smooth data regions in a 2 MB binary in ~12s, then AI confirms table type with byte-level reasoning. Built to avoid a class of dangerous tuning mistakes.',
      meta: 'Background job · safer than trusting XDFs',
      color: 'cyan',
    },
  ];

  const accent = {
    emerald: 'border-emerald-500/40 hover:border-emerald-400/70 from-emerald-500/5',
    purple:  'border-purple-500/40 hover:border-purple-400/70 from-purple-500/5',
    cyan:    'border-cyan-500/40 hover:border-cyan-400/70 from-cyan-500/5',
  };
  const tagColor = {
    emerald: 'text-emerald-400',
    purple:  'text-purple-300',
    cyan:    'text-cyan-400',
  };

  return (
    <section id="proof" className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#0D0D0D] to-[#0A0A0A] border-t border-[#18181B]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mx-auto text-center mb-12">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// Proof</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            Receipts, not screenshots.
          </h2>
          <p className="text-lg text-zinc-400">
            Every tune is iterated against real datalogs. Here are three things the platform has actually done — not what it might do.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {receipts.map((r) => (
            <motion.div
              key={r.title}
              {...FADE_UP}
              className={`p-6 rounded-sm border bg-gradient-to-b ${accent[r.color]} to-[#0F0F0F] transition-all hover:-translate-y-0.5`}
              data-testid={`proof-card-${r.color}`}
            >
              <div className={`text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.22em] ${tagColor[r.color]} mb-3`}>
                // {r.tag}
              </div>
              <div className="font-['Outfit'] text-lg sm:text-xl font-bold text-white mb-3 leading-tight">
                {r.title}
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed mb-4">
                {r.bullet}
              </p>
              <div className={`text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider ${tagColor[r.color]} border-t border-[#27272A] pt-3 mt-2`}>
                {r.meta}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Single testimonial — real beta tester */}
        <motion.div {...FADE_UP} className="mt-14 max-w-3xl mx-auto p-8 bg-gradient-to-br from-[#007AFF]/5 to-transparent border-l-2 border-[#007AFF]/50" data-testid="proof-testimonial">
          <p className="font-['Outfit'] text-xl sm:text-2xl text-zinc-200 leading-relaxed mb-4">
            "I'd been chasing a high-RPM AFR problem on my 328xi for months. I uploaded the binary, the platform spotted my XDF was wrong about which map was the WOT lambda table, and we iterated V8 → V12 over a few weekends — datalog in, scorecard out, generate, flash, repeat. Mid-range now hits target."
          </p>
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500">Jeff P. — Beta tester, 2010 BMW 328xi (N51 / MSV80)</div>
        </motion.div>
      </div>
    </section>
  );
}


// ========== DATABASE & COVERAGE ==========
function Coverage() {
  const makes = [
    { icon: Car, label: 'BMW' }, { icon: Car, label: 'Audi' }, { icon: Car, label: 'VW' },
    { icon: Car, label: 'Mercedes' }, { icon: Truck, label: 'Ford' }, { icon: Truck, label: 'GM' },
    { icon: Car, label: 'Toyota' }, { icon: Car, label: 'Honda' },
    { icon: Bike, label: 'Harley' }, { icon: Bike, label: 'Yamaha' },
    { icon: Ship, label: 'Marine' }, { icon: Tractor, label: 'John Deere' },
  ];
  return (
    <section id="coverage" className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#040404] to-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="text-center max-w-3xl mx-auto mb-14">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// Database & Coverage</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">Coverage that's growing fast.</h2>
          <p className="text-lg text-zinc-400">BMW MSV80 is live and battle-tested today. Our database pre-loads thousands of known tables for these platforms — unknown binaries are explored via AI reasoning from first principles.</p>
        </motion.div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 max-w-4xl mx-auto mb-10">
          {makes.map((m) => (
            <div key={m.label} className="group p-5 bg-[#121212] border border-[#27272A] rounded-sm flex flex-col items-center gap-2 hover:border-[#007AFF]/40 transition-all">
              <m.icon className="w-6 h-6 text-zinc-500 group-hover:text-[#007AFF] transition-colors" />
              <span className="text-xs font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-600 group-hover:text-white transition-colors">{m.label}</span>
            </div>
          ))}
        </div>
        <motion.div {...FADE_UP} className="text-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-[#FF5E1A]/10 to-[#007AFF]/10 border border-[#007AFF]/30 rounded-sm">
            <Sparkles className="w-5 h-5 text-[#007AFF]" />
            <span className="font-['Outfit'] text-lg sm:text-xl font-bold text-white">+ Heuristic discovery for unknown binaries</span>
          </div>
          <p className="text-sm text-zinc-500 mt-6 max-w-xl mx-auto">If it has an ECU binary, we can tune it. Help us grow — upload a Damos or XDF from your vehicle and earn credits + a contributor badge.</p>
        </motion.div>
      </div>
    </section>
  );
}

// ========== PRICING TEASER ==========
function PricingTeaser({ onPricing }) {
  const tunes = [
    { name: 'Single Tune',  price: '$250',   sub: 'One car · one calibration',
      includes: 'Stage 1/2, AI Copilot, unlimited revisions until you Green-Light', forWhom: 'enthusiast with one car' },
    { name: 'Dual Tune',    price: '$450',   sub: 'Same car · 2 calibrations', featured: true,
      includes: 'E85 + pump gas calibration, switchable, both with unlimited revisions', forWhom: 'dual-fuel daily/track use' },
    { name: 'Shop Pack',    price: '$1,500', sub: '10 tunes · $150 each',
      includes: '10 tunes, shop-account dashboard, customer-handoff flow', forWhom: 'small shops & tuning resellers' },
  ];
  const hardware = [
    { name: 'Smart Datalogger',     price: '$199', sub: 'WiFi · standalone',
      includes: 'WiFi datalogger, auto-upload, 1-yr warranty', forWhom: 'BYO flasher users' },
    { name: 'AI Tuner Pro Flasher', price: '$499', sub: 'OBD flasher · ships 2–3 wk',
      includes: 'OBD flasher, license-locked to your VIN', forWhom: 'first-time tuners' },
    { name: 'Datalogger + Tune',    price: '$425', sub: 'WiFi · saves $24', featured: true,
      includes: 'Smart Datalogger + your first tune', forWhom: 'most users (best ratio)' },
    { name: 'Ultimate Bundle',      price: '$675', sub: 'saves $473 · ships 2–3 wk',
      includes: 'Flasher + Datalogger + Dual Tune', forWhom: 'committed enthusiasts' },
  ];

  const goToPricing = () => {
    if (typeof window !== 'undefined') window.location.href = '/green-light#pricing';
  };

  return (
    <section id="pricing" className="py-24 bg-[#0A0A0A] border-t border-[#18181B]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mx-auto text-center mb-8">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// Pricing</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            One price. All revisions included.
          </h2>
          <p className="text-lg text-zinc-400">
            Unlimited revisions until YOU Green-Light the tune. Free ECU recovery during our process.
          </p>
        </motion.div>

        {/* ---------- MegaSquirt Banner (big, near top) ---------- */}
        <motion.a
          href="/green-light#megasquirt"
          {...FADE_UP}
          data-testid="megasquirt-top-banner"
          className="group block relative overflow-hidden mb-10 max-w-5xl mx-auto rounded-sm border border-emerald-500/40 bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 to-emerald-500/15 hover:border-emerald-400/70 hover:from-emerald-500/25 hover:via-emerald-500/10 transition-all"
        >
          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-[15%] -translate-y-1/2 w-[320px] h-[320px] rounded-full bg-emerald-500/20 blur-[120px]" />
            <div className="absolute top-1/2 right-[10%] -translate-y-1/2 w-[280px] h-[280px] rounded-full bg-emerald-500/15 blur-[100px]" />
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 p-5 sm:p-6">
            <div className="flex-shrink-0 flex items-center gap-3">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
              <span className="text-[10px] sm:text-[11px] font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-emerald-300 font-bold">
                Open-ECU pricing
              </span>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-['Outfit'] text-xl sm:text-2xl lg:text-3xl font-black tracking-tight text-white leading-tight">
                MegaSquirt users get tunes for{' '}
                <span className="bg-gradient-to-r from-emerald-300 via-emerald-200 to-emerald-100 bg-clip-text text-transparent">$50</span>.
              </div>
              <div className="text-[11px] sm:text-xs text-zinc-400 mt-1">
                MS1 · MS2 · MS3 · MS3X · MicroSquirt — unlimited revisions, same AI Copilot, 80% off.
              </div>
            </div>
            <div className="flex-shrink-0">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-[#0A0A0A] text-xs font-bold tracking-[0.12em] uppercase rounded-sm group-hover:bg-emerald-400 group-hover:shadow-[0_0_28px_rgba(52,211,153,0.5)] transition-all">
                See Details <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </div>
          </div>
        </motion.a>

        {/* Tune Services — 3 cards */}
        <div className="mb-4 flex items-center gap-3 max-w-5xl mx-auto">
          <div className="h-px flex-1 bg-[#27272A]" />
          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-zinc-500">// Tune Services</span>
          <div className="h-px flex-1 bg-[#27272A]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-5xl mx-auto mb-10">
          {tunes.map((t) => (
            <motion.div
              key={t.name}
              {...FADE_UP}
              className={`p-6 rounded-sm border transition-all hover:-translate-y-0.5 ${
                t.featured
                  ? 'bg-gradient-to-b from-[#FF5E1A]/10 via-[#121212] to-[#121212] border-[#FF5E1A]/50 shadow-[0_0_30px_rgba(255,94,26,0.15)]'
                  : 'bg-[#121212] border-[#27272A]'
              }`}
              data-testid={`teaser-tune-${t.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-2">{t.sub}</div>
              <div className="font-['Outfit'] text-xl font-bold text-white mb-1">{t.name}</div>
              <div className="font-['Outfit'] text-3xl font-black text-white mb-3">{t.price}</div>
              {t.includes && (
                <div className="text-xs text-zinc-400 leading-relaxed border-t border-[#27272A] pt-3 mt-2">
                  <span className="text-[#FF5E1A] font-bold">Includes:</span> {t.includes}
                </div>
              )}
              {t.forWhom && (
                <div className="text-[10px] text-zinc-600 font-['JetBrains_Mono'] uppercase tracking-wider mt-2">
                  For: {t.forWhom}
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Hardware & Bundles — 4 cards */}
        <div className="mb-4 flex items-center gap-3 max-w-5xl mx-auto">
          <div className="h-px flex-1 bg-[#27272A]" />
          <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-zinc-500">// Hardware &amp; Bundles</span>
          <div className="h-px flex-1 bg-[#27272A]" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto mb-10">
          {hardware.map((h) => (
            <motion.div
              key={h.name}
              {...FADE_UP}
              className={`p-5 rounded-sm border transition-all hover:-translate-y-0.5 ${
                h.featured
                  ? 'bg-gradient-to-b from-[#FF5E1A]/10 via-[#121212] to-[#121212] border-[#FF5E1A]/50 shadow-[0_0_30px_rgba(255,94,26,0.15)]'
                  : 'bg-[#121212] border-[#27272A]'
              }`}
              data-testid={`teaser-hw-${h.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              <div className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-2">{h.sub}</div>
              <div className="font-['Outfit'] text-base font-bold text-white mb-1 leading-tight">{h.name}</div>
              <div className="font-['Outfit'] text-2xl font-black text-white mb-2">{h.price}</div>
              {h.includes && (
                <div className="text-[11px] text-zinc-400 leading-relaxed border-t border-[#27272A] pt-2 mt-2">
                  {h.includes}
                </div>
              )}
              {h.forWhom && (
                <div className="text-[9px] text-zinc-600 font-['JetBrains_Mono'] uppercase tracking-wider mt-1.5">
                  For: {h.forWhom}
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={onPricing || goToPricing}
            data-testid="pricing-see-all-btn"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all shadow-[0_0_30px_rgba(255,94,26,0.35)]"
          >
            See Full Pricing &amp; Checkout <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-[11px] text-zinc-500 mt-3 font-['JetBrains_Mono']">
            LTE upgrade available on all datalogger bundles (+$100)
          </p>
        </div>
      </div>
    </section>
  );
}

// ========== HONEST SCOPE — what the platform IS / IS NOT today ==========
function HonestScope() {
  const isItems = [
    { icon: UploadCloud,  text: 'Upload stock ECU binaries' },
    { icon: Cpu,          text: 'Generate AI-tuned files quickly' },
    { icon: Database,     text: 'Manage multiple customer vehicles' },
    { icon: History,      text: 'Store + organize tune revisions' },
    { icon: ShieldCheck,  text: 'Deliver finished tunes ready to flash' },
  ];
  const isNotItems = [
    { icon: Usb,          text: 'Flash ECUs directly (you use your own hardware)' },
    { icon: Activity,     text: 'Connect live to your vehicle' },
    { icon: Gauge,        text: 'Live data logging in the browser' },
    { icon: Wrench,       text: 'Replace professional tuning hardware' },
  ];
  return (
    <section id="scope" className="py-24 bg-[#0A0A0A] border-t border-[#18181B]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mb-12">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#007AFF] mb-3">// Honest Scope</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-5">
            The brain in the cloud.<br />
            Your tool is the hands at the car.
          </h2>
          <p className="text-lg text-zinc-400 max-w-2xl">
            We don't oversell. Here's exactly what AI Tuner Pro does today, and what it doesn't — so you know what to pair it with.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <motion.div {...FADE_UP} className="p-7 bg-[#121212] border border-emerald-500/20 rounded-sm" data-testid="scope-is">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-emerald-500/15 border border-emerald-500/40">
                <Brain className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-emerald-400">What it does NOW</div>
                <div className="font-['Outfit'] text-xl font-bold text-white">The portal IS</div>
              </div>
            </div>
            <ul className="space-y-3">
              {isItems.map((it) => (
                <li key={it.text} className="flex items-start gap-3 text-sm text-zinc-200">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{it.text}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...FADE_UP} transition={{ duration: 0.6, delay: 0.1 }} className="p-7 bg-[#121212] border border-amber-500/20 rounded-sm" data-testid="scope-is-not">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-amber-500/15 border border-amber-500/40">
                <HandMetal className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-amber-400">Intentionally NOT</div>
                <div className="font-['Outfit'] text-xl font-bold text-white">The portal does NOT</div>
              </div>
            </div>
            <ul className="space-y-3">
              {isNotItems.map((it) => (
                <li key={it.text} className="flex items-start gap-3 text-sm text-zinc-300">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>{it.text}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-zinc-500 mt-5 italic">
              Safe ECU flashing requires local hardware + direct vehicle communication. We keep that responsibility where it belongs — with you and your tool.
            </p>
          </motion.div>
        </div>

        {/* Workflow strip */}
        <motion.div {...FADE_UP} className="mt-10 grid grid-cols-1 sm:grid-cols-5 gap-3 text-center" data-testid="scope-workflow">
          {[
            { icon: UploadCloud, label: 'Upload Stock' },
            { icon: Sparkles,    label: 'AI Generates' },
            { icon: MessageSquare, label: 'Iterate w/ Logs' },
            { icon: ShieldCheck, label: 'Download Tune' },
            { icon: Usb,         label: 'Flash w/ Tool' },
          ].map((s, i) => (
            <div key={s.label} className="relative p-4 bg-[#121212] border border-[#27272A] rounded-sm">
              <s.icon className="w-5 h-5 text-[#007AFF] mx-auto mb-2" />
              <div className="text-xs text-zinc-300 font-medium">{s.label}</div>
              {i < 4 && <ArrowRight className="hidden sm:block absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-700" />}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ========== RECOMMENDED HARDWARE ==========
function RecommendedTools({ onUpload }) {
  const tiers = [
    {
      tier: 'Entry-Level',
      tone: 'zinc',
      tools: [
        { name: 'MPPS V18', blurb: 'Affordable USB OBD flasher, great for older ECUs', good: 'Best first tool' },
        { name: 'KESS V2', blurb: 'Wide vehicle support, beginner-friendly clones available', good: 'Workshop-ready' },
      ],
    },
    {
      tier: 'Customer-Friendly',
      tone: 'orange',
      featured: true,
      tools: [
        { name: 'AI Tuner Pro Flasher', blurb: 'Our branded OBD flasher. Pre-paired to your portal, tune files staged before shipment. Flashes via the companion mobile app — some steps still coordinated with our team.', good: 'Best for customer-friendly hardware', cta: 'See compatibility', href: 'https://www.autotuner.com/cdn/shop/files/at-one-compatibility-list.pdf' },
      ],
    },
    {
      tier: 'Professional',
      tone: 'blue',
      tools: [
        { name: 'K-TAG / KESSv3', blurb: 'Bench flashing, recovery from bad flashes. Advanced users only.', good: 'When OBD won\'t do' },
        { name: 'Autotuner Master', blurb: 'Full bench + boot mode + tricore. Pro tuning shop standard.', good: 'Workshop tier' },
      ],
    },
  ];

  const toneClass = (t) => ({
    zinc:   { bd: 'border-zinc-700/50',    bg: 'bg-zinc-500/5',    text: 'text-zinc-300',  chip: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40' },
    orange: { bd: 'border-[#FF5E1A]/40',  bg: 'bg-[#FF5E1A]/5',   text: 'text-[#FF5E1A]', chip: 'bg-[#FF5E1A]/15 text-[#FF5E1A] border-[#FF5E1A]/40' },
    blue:   { bd: 'border-[#007AFF]/40',  bg: 'bg-[#007AFF]/5',   text: 'text-[#007AFF]', chip: 'bg-[#007AFF]/15 text-[#007AFF] border-[#007AFF]/40' },
  })[t];

  return (
    <section id="hardware" className="py-24 bg-gradient-to-b from-[#0A0A0A] via-[#050505] to-[#0A0A0A]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mb-12">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">// Pair With Real Hardware</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            Tools that work with the portal.
          </h2>
          <p className="text-lg text-zinc-400 max-w-2xl">
            We don't sell hardware. We tell you exactly what works. Pick the tier that fits your skill + budget — the portal generates the tune, your tool puts it on the car.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {tiers.map((t) => {
            const c = toneClass(t.tone);
            return (
              <motion.div
                key={t.tier}
                {...FADE_UP}
                className={`relative p-6 ${c.bg} border ${c.bd} rounded-sm ${t.featured ? 'lg:scale-[1.02] shadow-[0_0_40px_rgba(255,94,26,0.15)]' : ''}`}
                data-testid={`tools-tier-${t.tier.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-6 px-2 py-0.5 bg-[#FF5E1A] text-white text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] rounded-sm">
                    Recommended
                  </div>
                )}
                <div className={`text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] mb-3 ${c.text}`}>{t.tier}</div>
                <div className="space-y-4">
                  {t.tools.map((tool) => (
                    <div key={tool.name} className="border-t border-[#18181B] pt-4 first:border-t-0 first:pt-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="font-['Outfit'] text-base font-bold text-white">{tool.name}</div>
                        <span className={`flex-shrink-0 px-2 py-0.5 text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider border rounded-sm ${c.chip}`}>{tool.good}</span>
                      </div>
                      <p className="text-xs text-zinc-400 leading-relaxed">{tool.blurb}</p>
                      {tool.cta && (
                        <a href={tool.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-[#FF5E1A] hover:text-[#FF7A42] font-['JetBrains_Mono'] uppercase tracking-wider">
                          {tool.cta} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.p {...FADE_UP} className="text-xs text-center text-zinc-600 font-['JetBrains_Mono'] uppercase tracking-wider mt-8">
          We are not affiliated with these manufacturers. Picks are based on real-world reliability for AI Tuner Pro's workflow.
        </motion.p>

        <motion.div {...FADE_UP} className="mt-8 text-center">
          <button
            onClick={onUpload}
            data-testid="hardware-upload-cta"
            className="inline-flex items-center gap-2 px-7 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white font-bold text-sm tracking-[0.12em] uppercase rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_0_35px_rgba(255,94,26,0.4)]"
          >
            Upload Your Bin — Get the Tune First
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-zinc-600 mt-3">Pick your hardware after you see the tune. No pressure.</p>
        </motion.div>
      </div>
    </section>
  );
}

// ========== COMPANION DESKTOP APPS ==========
function CompanionApps() {
  const apps = [
    {
      name: 'romHEX 14 (RX14)',
      tag: 'Desktop verifier',
      blurb: 'Free open-source desktop hex editor + A2L viewer. Use it to cross-check the tune we generate against your verified ECU definitions, visualize maps in 3D, and inspect raw bytes before you flash. Pairs with our cloud workflow — we generate, RX14 verifies.',
      bullets: [
        'Open the same .bin you download from us',
        'Import our verified A2L for your ECU SW',
        'Inspect, cross-check, then flash with confidence',
      ],
      ctaLabel: 'Download RX14 (free)',
      ctaHref: 'https://romhex14.com/',
      altLabel: 'GitHub source',
      altHref: 'https://github.com/ctabuyo/romHEX14-community',
      testid: 'companion-rx14',
    },
  ];

  return (
    <section id="companion-apps" className="py-20 bg-[#0A0A0A] border-t border-[#18181B]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mb-10">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#007AFF] mb-3">// Optional Desktop Tools</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-black tracking-tighter text-white mb-3">
            Verify in the cloud. Inspect on your desktop.
          </h2>
          <p className="text-base text-zinc-400 max-w-2xl">
            Power users like to double-check map values before flashing. These free desktop tools complement the portal — open our generated bins, inspect bytes, visualize maps. We don't replace them and they don't replace us; they sit side-by-side.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {apps.map((a) => (
            <motion.div
              key={a.name}
              {...FADE_UP}
              data-testid={a.testid}
              className="relative p-6 bg-[#007AFF]/5 border border-[#007AFF]/30 rounded-sm hover:border-[#007AFF]/60 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="font-['Outfit'] text-xl font-bold text-white">{a.name}</div>
                  <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#007AFF] mt-1">{a.tag}</div>
                </div>
                <span className="flex-shrink-0 px-2 py-0.5 text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider border border-zinc-700 text-zinc-400 rounded-sm">
                  Partner tool
                </span>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed mb-4">{a.blurb}</p>
              <ul className="space-y-1.5 mb-5">
                {a.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-xs text-zinc-300">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#007AFF] mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href={a.ctaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${a.testid}-download-btn`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#007AFF] hover:bg-[#4DA3FF] text-white font-['JetBrains_Mono'] text-[11px] uppercase tracking-wider rounded-sm transition-colors"
                >
                  {a.ctaLabel} <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href={a.altHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${a.testid}-alt-link`}
                  className="text-[11px] text-zinc-400 hover:text-zinc-200 font-['JetBrains_Mono'] uppercase tracking-wider"
                >
                  {a.altLabel} →
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p {...FADE_UP} className="text-[11px] text-center text-zinc-600 font-['JetBrains_Mono'] uppercase tracking-wider mt-8">
          We are not affiliated with these projects. Listed because they actually help our users verify our work.
        </motion.p>
      </div>
    </section>
  );
}

// ========== ROADMAP ==========
function Roadmap() {
  const items = [
    { icon: Smartphone, title: 'Integrated flashing agent', sub: 'Windows desktop + iOS/Android — flash directly from the portal via your own hardware', status: 'In design' },
    { icon: Activity,   title: 'Live data logging',         sub: 'Real-time gauges streaming from supported adapters in your browser', status: 'In testing' },
    { icon: Cloud,      title: 'Live flash automation',     sub: 'Customer plugs in → portal streams tune → device flashes automatically. Partially implemented today (some steps still manually coordinated); full automation tracking vendor SDK availability.', status: 'In progress' },
    { icon: Server,     title: 'Cloud-to-device flashing',  sub: 'Encrypted end-to-end via AT Cloud. No file ever leaves the portal in plaintext.', status: 'Planned' },
    { icon: Activity,   title: 'AI Tuner Pro Smart Datalogger', sub: 'Pi-based onboard logger. 100Hz CAN-aware capture, WiFi auto-upload, edge knock/AFR analysis. Datalogging only — no crypto licensing required.', status: 'Reservations open', href: '/datalogger' },
    { icon: Database,   title: 'Coverage expansion',        sub: '4,000+ contributed Damos/A2L/XDF definitions in the seed pipeline', status: 'Active' },
  ];
  return (
    <section id="roadmap" className="py-24 bg-[#0A0A0A] border-t border-[#18181B]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mb-12">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#007AFF] mb-3">// Roadmap</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            What's coming next.
          </h2>
          <p className="text-lg text-zinc-400 max-w-2xl">
            No vapor. Each item below is either in design, testing, or active development. Updates land in the portal as soon as they're real.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => {
            const Wrapper = it.href ? 'a' : 'div';
            const wrapperProps = it.href ? { href: it.href } : {};
            return (
              <motion.div
                key={it.title}
                {...FADE_UP}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`roadmap-item-${i}`}
              >
                <Wrapper
                  {...wrapperProps}
                  className={`block group p-5 bg-[#121212] border border-[#27272A] rounded-sm transition-all ${
                    it.href ? 'hover:border-[#FF5E1A]/50 cursor-pointer' : 'hover:border-[#007AFF]/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-sm ${
                      it.href
                        ? 'bg-[#FF5E1A]/10 border border-[#FF5E1A]/40 group-hover:bg-[#FF5E1A]/20'
                        : 'bg-[#007AFF]/10 border border-[#007AFF]/30 group-hover:bg-[#007AFF]/20'
                    } transition-colors`}>
                      <it.icon className={`w-4 h-4 ${it.href ? 'text-[#FF5E1A]' : 'text-[#007AFF]'}`} />
                    </div>
                    <span className={`text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] px-2 py-0.5 border rounded-sm bg-[#0A0A0A] ${
                      it.href ? 'border-[#FF5E1A]/40 text-[#FF5E1A]' : 'border-[#27272A] text-zinc-400'
                    }`}>
                      {it.status}
                    </span>
                  </div>
                  <h3 className="font-['Outfit'] text-base font-bold text-white mb-1.5 flex items-center gap-1.5">
                    {it.title}
                    {it.href && <span className="text-[#FF5E1A] text-xs">→</span>}
                  </h3>
                  <p className="text-xs text-zinc-400 leading-relaxed">{it.sub}</p>
                </Wrapper>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ========== FOOTER ==========
function Footer() {
  return (
    <footer className="bg-[#050505] border-t border-[#18181B] py-12">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row gap-6 justify-between items-start text-xs text-zinc-600 font-['JetBrains_Mono']">
          <div>
            <div className="font-['Outfit'] text-xl font-black text-white mb-2">AI TUNER PRO</div>
            <div className="uppercase tracking-wider">Universal AI ECU tuning • Hybrid Database × AI</div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 uppercase tracking-wider">
            <a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a>
            <a href="#scope" className="hover:text-white transition-colors">Scope</a>
            <a href="#hardware" className="hover:text-white transition-colors">Hardware</a>
            <a href="#coverage" className="hover:text-white transition-colors">Coverage</a>
            <a href="#roadmap" className="hover:text-white transition-colors">Roadmap</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="/datalogger" className="hover:text-[#FF5E1A] transition-colors">Smart Datalogger</a>
            <a href="/green-light" className="hover:text-[#FF5E1A] transition-colors">Green Light Tune</a>
            <a href="/blog" className="hover:text-[#FF5E1A] transition-colors">Blog</a>
            <a href="/story" className="hover:text-[#FF5E1A] transition-colors">Our Story</a>
            <a href="mailto:support@aitunerproportal.com" className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-[#18181B] text-[10px] text-zinc-700 uppercase tracking-wider font-['JetBrains_Mono'] space-y-2">
          <div>© {new Date().getFullYear()} AI Tuner Pro. ECU calibration involves risk. Always validate with datalogs. Not responsible for user-flashed tunes or resulting damage. "Stock backup before every flash" is the rule.</div>
          <div>AI Tuner Pro Flasher hardware is manufactured by AutoTuner USA under license. MyFlasher™ is a trademark of AutoTuner USA. All other product names belong to their respective owners.</div>
        </div>
      </div>
    </footer>
  );
}

// ========== SMART DATALOGGER TEASER ==========
function SmartDataloggerTeaser() {
  return (
    <section id="datalogger-teaser" className="py-24 bg-[#0A0A0A] border-t border-[#18181B] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(45deg, #FF5E1A 25%, transparent 25%), linear-gradient(-45deg, #FF5E1A 25%, transparent 25%)',
        backgroundSize: '40px 40px',
      }} />
      <div className="container mx-auto px-6 relative">
        <motion.div {...FADE_UP} className="mb-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 rounded-sm mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#FF5E1A]" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-[#FF5E1A]">What's to come · reservations open</span>
          </div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-4">
            The datalog bottleneck<br/>
            <span className="text-[#FF5E1A]">dies here.</span>
          </h2>
          <p className="text-lg text-zinc-400 max-w-2xl">
            Introducing the AI Tuner Pro Smart Datalogger — a purpose-built onboard telemetry unit that lives in your car, captures 100 Hz CAN data on every drive, and auto-uploads the moment you pull into WiFi. The AI scores the pull before you're out of the car.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <motion.div {...FADE_UP} className="space-y-3">
            {[
              { icon: Zap,        title: 'Plug + drive',         text: 'No laptop. No cables. No app to launch. The device wakes when you start the car.' },
              { icon: Brain,      title: 'AI scores every pull', text: 'Tune Scorecard + Mechanical Health Gate run the instant a trip lands — faster than your tuner can answer the phone.' },
              { icon: ShieldCheck, title: 'Read-only by design', text: 'It does NOT flash. Cannot flash. No OEM crypto. Pairs with the AI Tuner Pro Flasher for the flash side — complementary, not competitive.' },
              { icon: Cloud,      title: 'Auto-uploads on WiFi', text: 'Captures locally to a 4 GB buffer, uploads on return. LTE option for track & fleet.' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                {...FADE_UP}
                transition={{ delay: i * 0.05, duration: 0.5 }}
                className="flex items-start gap-4 p-4 bg-[#121212] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/40 transition-all"
              >
                <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-sm bg-[#FF5E1A]/10 border border-[#FF5E1A]/30">
                  <item.icon className="w-4 h-4 text-[#FF5E1A]" />
                </div>
                <div>
                  <div className="font-['Outfit'] text-base font-bold text-white mb-1">{item.title}</div>
                  <p className="text-sm text-zinc-400 leading-relaxed">{item.text}</p>
                </div>
              </motion.div>
            ))}
            <div className="pt-4 flex flex-wrap gap-3">
              <a
                href="/datalogger"
                data-testid="home-datalogger-learn-more"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold uppercase tracking-[0.14em] rounded-sm transition-all hover:shadow-[0_0_30px_rgba(255,94,26,0.4)]"
              >
                See the full pitch <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="/datalogger#reserve"
                data-testid="home-datalogger-reserve"
                className="inline-flex items-center gap-2 px-6 py-3 bg-transparent hover:bg-[#FF5E1A]/10 text-white text-xs font-bold uppercase tracking-[0.14em] rounded-sm border border-[#27272A] hover:border-[#FF5E1A]/40 transition-all"
              >
                Reserve from $199
              </a>
            </div>
            <p className="text-[10px] text-zinc-600 font-['JetBrains_Mono'] uppercase tracking-wider pt-2">
              No deposit · pricing locked for pre-launch reservations
            </p>
          </motion.div>

          <motion.div {...FADE_UP} transition={{ delay: 0.2, duration: 0.6 }} className="relative">
            <a href="/datalogger" className="block relative w-full aspect-[4/3] bg-[#0F0F12] border border-[#27272A] rounded-sm overflow-hidden hover:border-[#FF5E1A]/50 transition-all group">
              <div className="absolute inset-0 opacity-[0.06]" style={{
                backgroundImage: 'radial-gradient(circle at 25% 25%, #FF5E1A 0%, transparent 50%), radial-gradient(circle at 75% 75%, #00B4D8 0%, transparent 50%)',
              }} />
              {/* Device silhouette */}
              <div className="absolute left-[10%] top-1/2 -translate-y-1/2 w-[30%] h-[50%] bg-[#1A1A1F] border border-[#FF5E1A]/30 rounded-sm flex flex-col justify-between p-3 group-hover:border-[#FF5E1A]/60 transition-colors">
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF5E1A] animate-pulse" style={{ animationDelay: '0.3s' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.6s' }} />
                </div>
                <div className="text-[9px] text-zinc-500 font-['JetBrains_Mono'] uppercase">Telemetry Unit</div>
                <div className="text-[9px] text-[#FF5E1A] font-['JetBrains_Mono']">ai-tuner-pro v1</div>
              </div>
              {/* Streaming frames */}
              <div className="absolute right-0 top-0 bottom-0 w-[52%] flex flex-col justify-center gap-1 p-4 overflow-hidden">
                {[
                  { id: '0x316', label: 'RPM',     value: '4231',  color: 'text-emerald-400' },
                  { id: '0x329', label: 'AFR',     value: '11.42', color: 'text-[#FF5E1A]' },
                  { id: '0x153', label: 'IAT',     value: '78°F',  color: 'text-cyan-400' },
                  { id: '0x545', label: 'KNOCK',   value: '0.18V', color: 'text-emerald-400' },
                  { id: '0x430', label: 'TIMING',  value: '+22.5°', color: 'text-zinc-400' },
                  { id: '0x1A0', label: 'LTFT',    value: '-3.1%', color: 'text-emerald-400' },
                ].map((f, i) => (
                  <div
                    key={f.id}
                    className="flex items-center gap-3 text-[10px] sm:text-xs font-['JetBrains_Mono'] py-1 border-l-2 border-[#27272A] pl-3 animate-pulse"
                    style={{ animationDelay: `${i * 0.2}s`, animationDuration: '2s' }}
                  >
                    <span className="text-zinc-600 w-12">{f.id}</span>
                    <span className="text-zinc-400 w-16">{f.label}</span>
                    <span className={`${f.color} font-bold`}>{f.value}</span>
                  </div>
                ))}
              </div>
              {/* Connector */}
              <svg className="absolute left-[40%] top-1/2 -translate-y-1/2 w-[8%] h-1" viewBox="0 0 100 4" preserveAspectRatio="none">
                <line x1="0" y1="2" x2="100" y2="2" stroke="#FF5E1A" strokeWidth="1" strokeDasharray="4 4">
                  <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="0.8s" repeatCount="indefinite" />
                </line>
              </svg>
              <div className="absolute bottom-3 left-3 text-[9px] font-['JetBrains_Mono'] text-zinc-600 uppercase tracking-widest">
                Live demo · 100 Hz CAN capture
              </div>
              <div className="absolute top-3 right-3 text-[9px] font-['JetBrains_Mono'] text-[#FF5E1A] uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                Learn more <ArrowRight className="w-3 h-3" />
              </div>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ========== TRIAL & BETA TESTER OFFER ==========
function TrialBetaOffer() {
  const [trialEmail, setTrialEmail] = React.useState('');
  const [trialStatus, setTrialStatus] = React.useState('idle'); // idle | submitting | success | error
  const [betaEmail, setBetaEmail] = React.useState('');
  const [betaWhy, setBetaWhy] = React.useState('');
  const [betaStatus, setBetaStatus] = React.useState('idle');

  const submitTrial = async (e) => {
    e.preventDefault();
    if (!trialEmail || !trialEmail.includes('@')) return;
    setTrialStatus('submitting');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trial/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trialEmail, source: 'landing_banner' }),
      });
      if (!res.ok) throw new Error('Failed');
      setTrialStatus('success');
    } catch (err) {
      setTrialStatus('error');
    }
  };

  const submitBeta = async (e) => {
    e.preventDefault();
    if (!betaEmail || !betaEmail.includes('@')) return;
    setBetaStatus('submitting');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/beta/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: betaEmail, why: betaWhy, source: 'landing_banner' }),
      });
      if (!res.ok) throw new Error('Failed');
      setBetaStatus('success');
    } catch (err) {
      setBetaStatus('error');
    }
  };

  return (
    <section id="trial-beta" className="py-20 bg-gradient-to-b from-[#0A0A0A] via-[#0F0F0F] to-[#0A0A0A] border-t border-[#18181B]">
      <div className="container mx-auto px-6">
        <motion.div {...FADE_UP} className="max-w-3xl mx-auto text-center mb-10">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-emerald-400 mb-3">// Launch Offer</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-5xl font-black tracking-tighter text-white mb-3">
            Get 2 weeks free. Plus one free tune.
          </h2>
          <p className="text-base text-zinc-400 max-w-2xl mx-auto">
            New sign-ups get full portal access for 14 days <span className="text-white">— AI Copilot, datalog analysis, unlimited uploads, plus one free tune file delivered during the trial.</span> No credit card required.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
          {/* ---------- Trial card ---------- */}
          <motion.div
            {...FADE_UP}
            data-testid="trial-signup-card"
            className="relative p-6 sm:p-7 bg-gradient-to-b from-emerald-500/10 via-[#121212] to-[#121212] border border-emerald-500/40 rounded-sm shadow-[0_0_30px_rgba(52,211,153,0.12)]"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.7)]" />
              <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-emerald-300 font-bold">2-Week Free Trial</span>
            </div>
            <div className="font-['Outfit'] text-2xl font-black text-white mb-3 leading-tight">
              Start free. Decide later.
            </div>
            <ul className="space-y-2 mb-6 text-sm text-zinc-300">
              {[
                'Full portal access for 14 days',
                'One free tune file delivered during trial',
                'AI Copilot + datalog analysis included',
                'Unlimited uploads + tune revisions',
                'No credit card · cancel anytime',
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            {trialStatus === 'success' ? (
              <div data-testid="trial-success" className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-sm text-sm text-emerald-200">
                ✓ You&apos;re in. We&apos;ll email setup instructions within minutes. Watch your inbox (and check spam just in case).
              </div>
            ) : (
              <form onSubmit={submitTrial} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  required
                  value={trialEmail}
                  onChange={(e) => setTrialEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="trial-email-input"
                  className="flex-1 px-4 py-3 bg-[#0A0A0A] border border-[#27272A] rounded-sm text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={trialStatus === 'submitting'}
                  data-testid="trial-start-btn"
                  className="px-5 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0A0A0A] text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all shadow-[0_0_20px_rgba(52,211,153,0.35)]"
                >
                  {trialStatus === 'submitting' ? 'Starting…' : 'Start Trial'}
                </button>
              </form>
            )}
            {trialStatus === 'error' && (
              <div className="text-[11px] text-amber-300 mt-2 font-['JetBrains_Mono']">
                Couldn&apos;t reach the server. Try again or email us at hello@aitunerproportal.com
              </div>
            )}
            <p className="text-[11px] text-zinc-500 mt-3 font-['JetBrains_Mono']">
              After 14 days: account becomes read-only unless you upgrade or apply to beta.
            </p>
          </motion.div>

          {/* ---------- Beta tester card ---------- */}
          <motion.div
            {...FADE_UP}
            data-testid="beta-apply-card"
            className="relative p-6 sm:p-7 bg-gradient-to-b from-[#FF5E1A]/10 via-[#121212] to-[#121212] border border-[#FF5E1A]/40 rounded-sm shadow-[0_0_30px_rgba(255,94,26,0.12)]"
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2 rounded-full bg-[#FF5E1A] animate-pulse shadow-[0_0_8px_rgba(255,94,26,0.7)]" />
              <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.25em] text-[#FF5E1A] font-bold">Trial Expired? Apply for Beta</span>
            </div>
            <div className="font-['Outfit'] text-2xl font-black text-white mb-3 leading-tight">
              Help us build it. Keep your access.
            </div>
            <p className="text-sm text-zinc-300 mb-4 leading-relaxed">
              If you uploaded datalogs, exchanged Copilot messages, or sent us feedback during your trial — apply for the beta tester program. Approved testers keep portal access free, get tune deliveries at cost, and shape the roadmap.
            </p>
            <ul className="space-y-1.5 mb-5 text-xs text-zinc-300">
              {[
                'Free continued portal access after trial expires',
                'Tune files at cost (no markup) while in beta',
                'Direct line to the engineering team',
                'Early access to new ECU coverage + features',
                'Manual approval — applies reviewed within 48 hrs',
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#FF5E1A] mt-0.5 flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            {betaStatus === 'success' ? (
              <div data-testid="beta-success" className="p-3 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 rounded-sm text-sm text-orange-200">
                ✓ Application received. We review every one personally — expect a response within 48 hours.
              </div>
            ) : (
              <form onSubmit={submitBeta} className="space-y-2">
                <input
                  type="email"
                  required
                  value={betaEmail}
                  onChange={(e) => setBetaEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="beta-email-input"
                  className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#27272A] rounded-sm text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF5E1A] transition-colors"
                />
                <textarea
                  value={betaWhy}
                  onChange={(e) => setBetaWhy(e.target.value)}
                  rows={2}
                  placeholder="What ECU/vehicle? What have you uploaded so far? (optional)"
                  data-testid="beta-why-input"
                  className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#27272A] rounded-sm text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#FF5E1A] transition-colors resize-none"
                />
                <button
                  type="submit"
                  disabled={betaStatus === 'submitting'}
                  data-testid="beta-apply-btn"
                  className="w-full sm:w-auto px-5 py-2.5 bg-[#FF5E1A] hover:bg-[#FF7A42] disabled:opacity-50 text-white text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all shadow-[0_0_20px_rgba(255,94,26,0.3)]"
                >
                  {betaStatus === 'submitting' ? 'Sending…' : 'Apply for Beta'}
                </button>
              </form>
            )}
            {betaStatus === 'error' && (
              <div className="text-[11px] text-amber-300 mt-2 font-['JetBrains_Mono']">
                Couldn&apos;t reach the server. Try again or email hello@aitunerproportal.com
              </div>
            )}
          </motion.div>
        </div>

        <motion.p {...FADE_UP} className="text-center text-[11px] text-zinc-600 font-['JetBrains_Mono'] uppercase tracking-wider mt-8">
          Limited launch offer · no credit card · trial signups capped at 500
        </motion.p>
      </div>
    </section>
  );
}

// ========== ROOT ==========
export default function UniversalHome({ onUpload, onDemo, onPricing }) {
  return (
    <div className="bg-[#0A0A0A]">
      <Hero onUpload={onUpload} onDemo={onDemo} />
      <TrustBar />
      <TrialBetaOffer />
      <HowItWorks />
      <Features />
      <HonestScope />
      <RecommendedTools onUpload={onUpload} />
      <CompanionApps />
      <Proof />
      <Coverage />
      <SmartDataloggerTeaser />
      <Roadmap />
      <PricingTeaser onPricing={onPricing} />
      <Footer />
    </div>
  );
}
